import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { firebaseAuthService } from '../services/firebaseAuthService';
import { ShieldCheckIcon, UserIcon, EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { isGoogleProviderEnabled } from '../utils/authUtils';

const registerSchema = z.object({
    firstName: z.string().min(2, 'First name is required'),
    lastName: z.string().min(2, 'Last name is required'),
    email: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(6, 'Confirm password must be at least 6 characters'),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

const Register: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isValidatingInvitation, setIsValidatingInvitation] = useState(false);
    const [invitationError, setInvitationError] = useState<string | null>(null);

    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');
    const assessmentId = searchParams.get('assessmentId');
    const workspaceAuth = searchParams.get('workspace_auth') === 'true';

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<RegisterFormData>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            email: emailParam || '',
        }
    });

    useEffect(() => {
        let isMounted = true;

        const validateInvitation = async () => {
            if (!token) {
                setInvitationError(null);
                return;
            }

            setIsValidatingInvitation(true);
            setInvitationError(null);

            try {
                const response = await apiService.get<{
                    success: boolean;
                    data?: { email?: string; firstName?: string; lastName?: string; workspaceAuthOnly?: boolean };
                }>(`/auth/verify-invitation/${token}`);

                const invitedEmail = response.data?.email;
                if (!invitedEmail) {
                    throw new Error('Invalid invitation response');
                }

                if (emailParam && emailParam.toLowerCase() !== invitedEmail.toLowerCase()) {
                    throw new Error('Invitation email does not match the provided email.');
                }

                if (isMounted) {
                    setValue('email', invitedEmail);
                    if (response.data?.firstName) {
                        setValue('firstName', response.data.firstName);
                    }
                    if (response.data?.lastName) {
                        setValue('lastName', response.data.lastName);
                    }
                }
            } catch (error: any) {
                const message =
                    error?.response?.data?.error?.message ||
                    error?.message ||
                    'Invalid or expired invitation link.';
                if (isMounted) {
                    setInvitationError(message);
                }
            } finally {
                if (isMounted) {
                    setIsValidatingInvitation(false);
                }
            }
        };

        validateInvitation();

        return () => {
            isMounted = false;
        };
    }, [token, emailParam, setValue]);

    const handleGoogleCompleteRegistration = async () => {
        if (invitationError || !token) {
            toast.error(invitationError || 'Invalid invitation');
            return;
        }
        setIsLoading(true);
        try {
            const result = await firebaseAuthService.loginWithGoogle();
            if (result?.user) {
                toast.success('Registration complete!');
                if (assessmentId) {
                    navigate(`/assessments/${assessmentId}`);
                } else {
                    navigate('/dashboard');
                }
            }
        } catch (error: any) {
            console.error('Google sign-in failed:', error);
            toast.error(error.message || 'Sign-in failed');
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (data: RegisterFormData) => {
        if (token && invitationError) {
            toast.error(invitationError);
            return;
        }

        setIsLoading(true);
        try {
            await firebaseAuthService.register({
                email: data.email,
                password: data.password,
                firstName: data.firstName,
                lastName: data.lastName,
                invitationToken: token || undefined,
            });

            toast.success('Registration successful!');

            // Redirect to assessment if ID provided, otherwise dashboard
            if (assessmentId) {
                navigate(`/assessments/${assessmentId}`);
            } else {
                navigate('/dashboard');
            }
        } catch (error: any) {
            console.error('Registration failed:', error);
            toast.error(error.message || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="flex justify-center">
                    <div className="p-3 bg-primary-600 rounded-xl shadow-lg">
                        <ShieldCheckIcon className="h-10 w-10 text-white" />
                    </div>
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-neutral-900">
                    Complete Your Registration
                </h2>
                <p className="mt-2 text-center text-sm text-neutral-600">
                    Join the CAPRI Compass community
                </p>
                {token && (
                    <p className="mt-2 text-center text-xs text-neutral-500">
                        {workspaceAuth ? 'Google Workspace sign-in only' : 'Invitation-based registration'}
                    </p>
                )}
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10 border border-neutral-200">
                    {workspaceAuth && token && !invitationError && !isValidatingInvitation && (
                        <div className="space-y-4">
                            <p className="text-sm text-neutral-600 text-center">
                                Complete your registration by signing in with your Google Workspace account. No password is needed.
                            </p>
                            {isGoogleProviderEnabled() ? (
                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={handleGoogleCompleteRegistration}
                                        disabled={isLoading}
                                        className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-neutral-300 rounded-lg shadow-sm text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                                    >
                                        {isLoading ? (
                                            <LoadingSpinner size="sm" />
                                        ) : (
                                            <>
                                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                                </svg>
                                                Sign in with Google
                                            </>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <p className="text-sm text-amber-600 text-center">Google sign-in is not enabled for this environment.</p>
                            )}
                        </div>
                    )}
                    {token && isValidatingInvitation && (
                        <div className="mb-4 flex items-center text-sm text-neutral-600">
                            <LoadingSpinner size="sm" className="mr-2" />
                            Validating invitation...
                        </div>
                    )}
                    {token && invitationError && (
                        <div className="mb-4 rounded-md border border-error-200 bg-error-50 p-3 text-sm text-error-700">
                            {invitationError}
                        </div>
                    )}
                    {!(workspaceAuth && token && !invitationError) && (
                    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700">First Name</label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <UserIcon className="h-5 w-5 text-neutral-400" />
                                    </div>
                                    <input
                                        {...register('firstName')}
                                        className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
                                        placeholder="John"
                                    />
                                    {errors.firstName && <p className="mt-1 text-xs text-error-600">{errors.firstName.message}</p>}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700">Last Name</label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <UserIcon className="h-5 w-5 text-neutral-400" />
                                    </div>
                                    <input
                                        {...register('lastName')}
                                        className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
                                        placeholder="Doe"
                                    />
                                    {errors.lastName && <p className="mt-1 text-xs text-error-600">{errors.lastName.message}</p>}
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-neutral-700">Email Address</label>
                            <div className="mt-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <EnvelopeIcon className="h-5 w-5 text-neutral-400" />
                                </div>
                                <input
                                    {...register('email')}
                                    type="email"
                                    disabled={!!emailParam || !!token}
                                    className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm disabled:bg-neutral-50 disabled:text-neutral-500"
                                    placeholder="john.doe@example.com"
                                />
                                {errors.email && <p className="mt-1 text-xs text-error-600">{errors.email.message}</p>}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-neutral-700">Password</label>
                            <div className="mt-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LockClosedIcon className="h-5 w-5 text-neutral-400" />
                                </div>
                                <input
                                    {...register('password')}
                                    type="password"
                                    className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
                                    placeholder="••••••••"
                                />
                                {errors.password && <p className="mt-1 text-xs text-error-600">{errors.password.message}</p>}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-neutral-700">Confirm Password</label>
                            <div className="mt-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LockClosedIcon className="h-5 w-5 text-neutral-400" />
                                </div>
                                <input
                                    {...register('confirmPassword')}
                                    type="password"
                                    className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
                                    placeholder="••••••••"
                                />
                                {errors.confirmPassword && <p className="mt-1 text-xs text-error-600">{errors.confirmPassword.message}</p>}
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={isLoading || isValidatingInvitation || !!invitationError}
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-all shadow-lg hover:shadow-xl"
                            >
                                {isLoading ? (
                                    <>
                                        <LoadingSpinner size="sm" className="mr-2 text-white" />
                                        Registering...
                                    </>
                                ) : (
                                    'Complete Registration'
                                )}
                            </button>
                        </div>
                    </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Register;
